import { Flex } from 'antd'
import React, { useRef, useEffect, useState } from 'react'
import Left from './station/Left'
import EChartComponent from '@/components/EChartComponent'
import { screenFull } from '@/utils'
import useOneStationStore from '@/stores/oneStation'
import useGlobalConfigStore from '@/stores/globalConfig'
import useMessage from '@/hooks/useMessage'
import { VirtualDataProps, WeatherDataProps } from '@/type/publicPropsType'
import getWeather from '@/utils/getWeather'
import { getLabelData, calcVirtualData, calcVirtualByInterval } from '@/utils/getData'
import getTimeStamp from '@/utils/getTimeStamp'
import { StationVirtualPoint } from '@/type/stationType'
import { useTranslation } from 'react-i18next'

const Station: React.FC = () => {
  const stationState = useOneStationStore()
  const { t } = useTranslation()
  const initFinished = useGlobalConfigStore(state => state.initFinished)
  const setUpdateTime = useGlobalConfigStore(state => state.setUpdateTime)
  const { message } = useMessage()

  const [weatherState, setWeatherState] = useState<WeatherDataProps>({
    temp: '',
    weather: 'Loading',
  })
  const [leftData, setLeftData] = useState<VirtualDataProps[]>([])
  const [trendDynamicOption, setTrendDynamicOption] = useState<any>({})
  const [leftPieDynamicOption, setLeftPieDynamicOption] = useState<any>({})
  const [rightPieDynamicOption, setRightPieDynamicOption] = useState<any>({})
  const [chargeSum, setChargeSum] = useState(0)
  const [useSum, setUseSum] = useState(0)
  const dataTimer = useRef<NodeJS.Timeout | null>(null),
    weatherTimer = useRef<NodeJS.Timeout | null>(null)
  const chartRef = useRef<echarts.ECharts | null>(null)
  const trendStaticOption = useRef<echarts.EChartsOption>({
    tooltip: {
      trigger: 'axis',
    },
    grid: {
      top: 40,
      left: 45,
      bottom: 25,
      right: 30,
    },
    dataZoom: [
      { type: 'inside' }, //用于添加滚轮缩放
    ],
    legend: {
      show: true,
      top: 0,
      left: -3,
      itemGap: 13,

      textStyle: {
        fontSize: 10,
        color: '#000',
      },
      type: 'scroll',
      pageIconColor: '#000',
      pageIconInactiveColor: '#999',
      pageIconSize: 10,
      pageTextStyle: {
        fontSize: 12,
        color: '#000',
      },
      scrollDataIndex: 0,
      orient: 'horizontal',
      padding: [0, 20],
      inactiveColor: '#999',
      selectedMode: 'multiple',
    },
    xAxis: {
      type: 'time',
      axisLabel: {
        fontSize: '0.7em',
        margin: 15,
      },
    },
    yAxis: {
      axisLabel: {
        fontSize: 12,
      },
      nameTextStyle: {
        color: '#aaa',
        fontSize: 8,
      },
      nameLocation: 'end',
      nameGap: 10,
    },
  })
  const pieStaticOption = useRef<echarts.EChartsOption>({
    legend: {
      top: 'bottom',
      left: 'left', // 将图例居左
      padding: [0, 0, 25, 15],
      itemGap: 12,

      textStyle: {
        width: 100,
        fontSize: 12,
      },
      show: true,
    },
  })

  const computedVirtualData = async (virtualType: string, propertyCode: string, type: string) => {
    let label = stationState.station_virtual_label.filter((e: any) => e.type === virtualType)
    let point: StationVirtualPoint[] = []
    label.forEach(e => {
      e.points.forEach(p => {
        if (p.property_code === propertyCode) {
          point.push(p)
        }
      })
    })
    return await calcVirtualData(point, type)
  }

  const initLeftData = async () => {
    let res: any
    //  电
    try {
      let points: (StationVirtualPoint & { label_name: string })[] = []
      stationState.station_virtual_label.map(label => {
        label.points.map(point => {
          if (
            point.property_code === 'P' ||
            point.property_code === 'SOC' ||
            point.property_code === 'EV-P' ||
            point.property_code === 'HP-P' ||
            point.property_code === 'IL-P' ||
            point.property_code === 'PF-P' ||
            point.property_code === 'WH-P'
          ) {
            points.push({ ...point, label_name: label.name })
          }
        })
      })
      // console.log(points)
      let leftData: VirtualDataProps[] = []
      res = await getLabelData(points, stationState.access_device, stationState.gateway, stationState.mqtt_serve)
      leftData = [...res]
      // res = await computedVirtualData('fd', 'KRA', 'day')
      // leftData.push({ value: Number(res.value), label_name: 'DailyPV', unit: res.unit })
      res = await computedVirtualData('fd', 'today-KRA', 'day')
      leftData.push({ value: Number(res.value), label_name: 'DailyPV', unit: res.unit })
      res = await computedVirtualData('cn', 'today-KA', 'day')
      leftData.push({ value: Number(res.value), label_name: 'DailyCharge', unit: res.unit })
      res = await computedVirtualData('fz', 'today-KA', 'day')
      leftData.push({ value: Number(res.value), label_name: 'DailyConsumption', unit: res.unit })
      res = await computedVirtualData('sd', 'today-KRA', 'day')
      leftData.push({ value: Number(res.value), label_name: 'DailySales', unit: res.unit })
      // console.log(leftData)
      setLeftData(leftData)
      setUpdateTime()
    } catch (e) {
      console.log(e)
      message.error('Get Power Data Error')
    }
    // console.log(res)
  }

  const initRightTopData = async () => {
    let res: { data: { value: number; time: number }[]; unit: string }, point: StationVirtualPoint
    const currentDate = new Date()
    const { startTime, endTime } = getTimeStamp('day', currentDate)
    const trendD: {
      solarProduction: [number, string][]
      consumption: [number, string][]
      selfConsumption: [number, string][]
      unit: string
    } = { solarProduction: [], consumption: [], selfConsumption: [], unit: '' }

    try {
      //  发电
      point = stationState.station_virtual_label
        .find(label => label.type === 'fd')
        ?.points.find(point => point.property_code === 'KRA') as StationVirtualPoint
      res = await calcVirtualByInterval(point, startTime, endTime, '5min', 'diff')
      trendD.solarProduction = res.data.map(item => [item.time, item.value.toFixed(2)])
      trendD.unit = res.unit

      // console.log(res)

      //  充电
      point = stationState.station_virtual_label
        .find(label => label.type === 'cn')
        ?.points.find(point => point.property_code === 'KA') as StationVirtualPoint
      res = await calcVirtualByInterval(point, startTime, endTime, '5min', 'diff')
      const charge = res.data.map(item => item.value)

      // console.log(res)

      // //  放电
      // point = stationState.station_virtual_label
      //   .find((label) => label.type === "cn")
      //   ?.points.find((point) => point.property_code === "KRA") as StationVirtualPoint
      // res = await calcVirtualByInterval(point, startTime, endTime, "5min","diff")
      // const disCharge = res.data.map((item) => item.value)

      // // console.log(res)

      //  卖电
      point = stationState.station_virtual_label
        .find(label => label.type === 'sd')
        ?.points.find(point => point.property_code === 'KRA') as StationVirtualPoint
      res = await calcVirtualByInterval(point, startTime, endTime, '5min', 'diff')
      trendD.consumption = res.data.map(item => [item.time, item.value.toFixed(2)])

      // console.log(res)

      // //  买电
      // point = stationState.station_virtual_label
      //   .find((label) => label.type === "sd")
      //   ?.points.find((point) => point.property_code === "KA") as StationVirtualPoint
      // res = await calcVirtualByInterval(point, startTime, endTime, "5min","diff")
      // const buy = res.data.map((item) => item.value)

      // // console.log(res)

      //  用电
      for (var i = 0; i < trendD.solarProduction.length; i++) {
        if (Number(trendD.solarProduction[i][1]) < charge[i]) {
          charge[i] = 0
        }

        //  并网
        if (Number(trendD.solarProduction[i][1]) - charge[i] < Number(trendD.consumption[i][1])) {
          trendD.consumption[i][1] = (Number(trendD.solarProduction[i][1]) - charge[i]).toFixed(2)
        }

        //  自发自用
        trendD.selfConsumption.push([
          trendD.solarProduction[i][0],
          (Number(trendD.solarProduction[i][1]) - charge[i] - Number(trendD.consumption[i][1])).toFixed(2),
        ])
      }

      // console.log(trendD)

      const trendDynamicOptionTemp: echarts.EChartsOption = {
        yAxis: {
          name: trendD.unit,
        },
        legend: {
          data: [t('site.solarProduction'), t('site.gridConsumption'), t('site.selfConsumption')],
        },
        series: [
          {
            name: t('site.solarProduction'),
            type: 'line',
            data: trendD.solarProduction,
            lineStyle: {
              width: 1,
            },
            emphasis: {
              focus: 'series',
            },
            areaStyle: {
              opacity: 0.3, // Use opacity to create transparency effect
            },
            symbol: 'none',
          },
          {
            name: t('site.gridConsumption'),
            type: 'line',
            data: trendD.consumption,
            lineStyle: {
              width: 1,
            },
            emphasis: {
              focus: 'series',
            },
            areaStyle: {
              opacity: 0.3, // Use opacity to create transparency effect
            },
            symbol: 'none',
          },
          {
            name: t('site.selfConsumption'),
            type: 'line',
            data: trendD.selfConsumption,
            lineStyle: {
              width: 1,
            },
            emphasis: {
              focus: 'series',
            },
            areaStyle: {
              opacity: 0.3, // Use opacity to create transparency effect
            },
            symbol: 'none',
          },
        ],
      }
      // console.log(trendOption)
      setTrendDynamicOption(trendDynamicOptionTemp)
    } catch (e) {
      console.log(e)
    }
  }

  const initRightBottomData = async () => {
    let res: { value: string | number; unit: string },
      leftData: { value: string | number; name: string }[] = [],
      rightData: { value: number | string; name: string }[] = []

    try {
      //  发电
      res = await computedVirtualData('fd', 'KRA', 'day')
      let pv = res.value
      //  充电
      res = await computedVirtualData('cn', 'KA', 'day')
      let charge = res.value
      //  放电
      res = await computedVirtualData('cn', 'KRA', 'day')
      let disCharge = res.value
      //  卖电
      res = await computedVirtualData('sd', 'KRA', 'day')
      let sale = res.value
      //  买电
      res = await computedVirtualData('sd', 'KA', 'day')
      let buy = res.value
      //  用于电池
      if (Number(pv) < Number(charge)) {
        charge = (0).toFixed(2)
      }
      //  用于电网
      if (Number(pv) - Number(charge) < Number(sale)) {
        sale = (Number(pv) - Number(charge)).toFixed(2)
      }
      leftData.push({ value: charge, name: t('site.toBattery') })
      leftData.push({ value: sale, name: t('site.toGrid') })
      leftData.push({ value: (Number(pv) - Number(charge) - Number(sale)).toFixed(2), name: t('site.toLoad') })
      rightData.push({ value: disCharge, name: t('site.fromBattery') })
      // console.log(pv,sale,buy,charge,disCharge)
      rightData.push({
        value: (Number(pv) - Number(sale) - Number(charge)).toFixed(2),
        name: t('site.fromPV'),
      })
      rightData.push({ value: buy, name: t('site.fromGrid') })
      const useSum = rightData.reduce((pre, cur) => pre + Number(cur.value), 0)

      // console.log(leftData, rightData)

      setLeftPieDynamicOption({
        series: [
          {
            name: 'Nightingale Chart',
            type: 'pie',
            radius: ['50%', '45%'], // 调整饼图的半径
            center: ['50%', '30%'], // 将饼图居中，40% 控制顶部距离

            itemStyle: {
              borderRadius: 8, // 扇形边角圆润
              borderWidth: 0, // 扇形无边框
            },
            label: {
              show: true,
              position: 'outside', // 标签显示在扇形外部
              // formatter: "{b}\n{d}%", // 标签格式
              formatter: '{d}%',
              lineHeight: 14, // 标签行高
              fontSize: 11, // 设置字体大小为10像素
              borderWidth: 0, // 无边框
              color: '#666666', // 标签文字颜色
            },
            labelLine: {
              show: true, // 显示指示线
              length: 14, // 指示线的长度
              length2: 20, // 指示线的第二段长度
              lineStyle: {
                color: '#cccccc', // 设置指示线的颜色
                width: 1, // 指示线的宽度
              },
            },
            data: leftData,
          },
        ],
      })
      setRightPieDynamicOption({
        series: [
          {
            name: 'Nightingale Chart',
            type: 'pie',
            radius: ['50%', '45%'], // 调整饼图的半径
            center: ['50%', '30%'], // 将饼图居中，40% 控制顶部距离

            itemStyle: {
              borderRadius: 8, // 扇形边角圆润
              borderWidth: 0, // 扇形无边框
            },
            label: {
              overflow: 'truncate',
              // ellipsis: false,
              show: true,
              position: 'outside', // 标签显示在扇形外部
              // formatter: "{b}\n{d}%", // 标签格式
              formatter: '{d}%',
              lineHeight: 15, // 标签行高
              fontSize: 11, // 设置字体大小为10像素
              borderWidth: 0, // 无边框
              color: '#666666', // 标签文字颜色
            },
            labelLine: {
              show: true, // 显示指示线
              length: 14, // 指示线的长度
              length2: 20, // 指示线的第二段长度
              lineStyle: {
                color: '#cccccc', // 设置指示线的颜色
                width: 1, // 指示线的宽度
              },
            },
            data: rightData,
          },
        ],
      })
      setChargeSum(Number(pv))
      setUseSum(useSum)
    } catch (e) {
      console.log(e)
    }
  }

  const initData = async () => {
    initWeatherData()
    await initLeftData()
    await initRightTopData()
    await initRightBottomData()
    if (weatherTimer.current) {
      clearInterval(weatherTimer.current)
    }
    weatherTimer.current = setInterval(initWeatherData, 1000 * 60 * 10)
    if (dataTimer.current) {
      clearInterval(dataTimer.current)
    }
    dataTimer.current = setInterval(async () => {
      await initLeftData()
      await initRightTopData()
      await initRightBottomData()
    }, 1000 * 20)
  }

  const initWeatherData = async () => {
    //  天气
    try {
      const res = await getWeather(stationState.station.latitude, stationState.station.longitude)
      setWeatherState(res)
      setUpdateTime()
    } catch (e) {
      console.log(e)
      message.error('Get Weather Error')
    }
  }

  useEffect(() => {
    // console.log(initFinished)
    if (initFinished) {
      // console.log(stationState)
      initData()
    }
    return () => {
      if (weatherTimer.current) {
        clearInterval(weatherTimer.current)
      }
      if (dataTimer.current) {
        clearInterval(dataTimer.current)
      }
    }
  }, [initFinished])

  // const option: echarts.EChartsOption = {
  //   tooltip: {
  //     trigger: "axis",
  //   },
  //   grid: {
  //     top: 40,
  //     left: 45,
  //     bottom: 25,
  //     right: 30,
  //   },
  //   legend: {
  //     show: true,
  //     data: ["Solar Production", "Consumption", "Self Consumption"],
  //     top: 0,
  //     left: -3,
  //     itemGap: 13,

  //     textStyle: {
  //       fontSize: 10,
  //       color: "#000",
  //     },
  //     type: "scroll",
  //     pageIconColor: "#000",
  //     pageIconInactiveColor: "#999",
  //     pageIconSize: 10,
  //     pageTextStyle: {
  //       fontSize: 12,
  //       color: "#000",
  //     },
  //     scrollDataIndex: 0,
  //     orient: "horizontal",
  //     padding: [0, 20],
  //     inactiveColor: "#999",
  //     selectedMode: "multiple",
  //   },
  //   xAxis: {
  //     data: ["00:00", "00:10", "00:20", "00:30", "00:40", "00:50"],
  //     boundaryGap: false,
  //     axisLabel: {
  //       fontSize: "0.7em",
  //       margin: 15,
  //     },
  //   },
  //   yAxis: {
  //     axisLabel: {
  //       fontSize: 12,
  //     },
  //   },
  //   series: [
  //     {
  //       name: "Solar Production",
  //       type: "line",
  //       data: [10, 20, 15, 30, 20, 150],
  //       lineStyle: {
  //         width: 3,
  //       },
  //       emphasis: {
  //         focus: "series",
  //       },
  //       areaStyle: {
  //         opacity: 0.3, // Use opacity to create transparency effect
  //       },
  //       symbol: "none",
  //     },
  //     {
  //       name: "Consumption",
  //       type: "line",
  //       data: [15, 25, 20, 351, 25, 30],
  //       lineStyle: {
  //         width: 3,
  //       },
  //       emphasis: {
  //         focus: "series",
  //       },
  //       areaStyle: {
  //         opacity: 0.3, // Use opacity to create transparency effect
  //       },
  //       symbol: "none",
  //     },
  //     {
  //       name: "Self Consumption",
  //       type: "line",
  //       data: [5, 10, 8, 125, 10, 12],
  //       lineStyle: {
  //         width: 3,
  //       },
  //       emphasis: {
  //         focus: "series",
  //       },
  //       areaStyle: {
  //         opacity: 0.3, // Use opacity to create transparency effect
  //       },
  //       symbol: "none",
  //     },
  //   ],
  // }
  // const borderRadiusOption: echarts.EChartsOption = {
  //   legend: {
  //     top: "bottom",
  //     left: "left", // 将图例居左
  //     padding: [0, 0, 25, 15],

  //     textStyle: {
  //       fontSize: 12,
  //     },
  //     show: true,
  //   },

  //   series: [
  //     {
  //       name: "Nightingale Chart",
  //       type: "pie",
  //       radius: ["50%", "45%"], // 调整饼图的半径
  //       center: ["50%", "30%"], // 将饼图居中，40% 控制顶部距离

  //       itemStyle: {
  //         borderRadius: 8, // 扇形边角圆润
  //         borderWidth: 0, // 扇形无边框
  //       },
  //       label: {
  //         show: true,
  //         position: "outside", // 标签显示在扇形外部
  //         formatter: "{b}\n{d}%", // 标签格式
  //         lineHeight: 14, // 标签行高
  //         fontSize: 11, // 设置字体大小为10像素
  //         borderWidth: 0, // 无边框
  //         color: "#666666", // 标签文字颜色
  //       },
  //       labelLine: {
  //         show: true, // 显示指示线
  //         length: 14, // 指示线的长度
  //         length2: 20, // 指示线的第二段长度
  //         lineStyle: {
  //           color: "#cccccc", // 设置指示线的颜色
  //           width: 1, // 指示线的宽度
  //         },
  //       },
  //       data: [
  //         { value: 50, name: "Use" },
  //         { value: 10, name: "Export" },
  //       ],
  //     },
  //   ],
  // }
  // let paddingBottom = 25
  // if (window.innerHeight < 800) {
  //   paddingBottom = 15
  // }
  // const borderRadiusOption2: echarts.EChartsOption = {
  //   legend: {
  //     top: "bottom",
  //     left: "left", // 将图例居左
  //     padding: [0, 0, paddingBottom, 15],
  //     itemGap: 12,

  //     textStyle: {
  //       width: 100,
  //       fontSize: 12,
  //     },
  //     show: true,
  //   },

  //   series: [
  //     {
  //       name: "Nightingale Chart",
  //       type: "pie",
  //       radius: ["50%", "45%"], // 调整饼图的半径
  //       center: ["50%", "30%"], // 将饼图居中，40% 控制顶部距离

  //       itemStyle: {
  //         borderRadius: 8, // 扇形边角圆润
  //         borderWidth: 0, // 扇形无边框
  //       },
  //       label: {
  //         show: true,
  //         position: "outside", // 标签显示在扇形外部
  //         formatter: "{b}\n{d}%", // 标签格式
  //         lineHeight: 15, // 标签行高
  //         fontSize: 11, // 设置字体大小为10像素
  //         borderWidth: 0, // 无边框
  //         color: "#666666", // 标签文字颜色
  //       },
  //       labelLine: {
  //         show: true, // 显示指示线
  //         length: 14, // 指示线的长度
  //         length2: 20, // 指示线的第二段长度
  //         lineStyle: {
  //           color: "#cccccc", // 设置指示线的颜色
  //           width: 1, // 指示线的宽度
  //         },
  //       },
  //       data: [
  //         { value: 25, name: "Solar" },
  //         { value: 25, name: "Gen" },
  //         { value: 25, name: "Battery" },
  //         { value: 25, name: "Grid" },
  //       ],
  //     },
  //   ],
  // }

  return (
    <div id='app-Index'>
      <Flex gap={15}>
        <div className='Index-left'>
          <Left
            powerData={leftData}
            {...weatherState}
            deviceState={stationState.device.find(item => item.device_type === 'C002')?.id}
          />
        </div>
        <div className='Index-right'>
          <div className='Index-right-top screen-full-box'>
            <div className='Index-right-title'>
              <span>{t('site.pac')}</span>
              <img
                className='qp'
                src='/assets/image/qp.svg'
                onClick={e => screenFull(e, chartRef.current, 'calc(100% - 60px)')}
              />
              <img
                className='fqp'
                style={{ display: 'none' }}
                src='/assets/image/fqp.svg'
                onClick={e => screenFull(e, chartRef.current, 'calc(100% - 60px)')}
              />
            </div>
            <div style={{ width: '100%', height: 'calc(100% - 60px)' }}>
              <EChartComponent
                // option={trendOption}
                staticOption={trendStaticOption.current}
                dynamicOption={trendDynamicOption}
                onChartInit={chartInstance => {
                  chartRef.current = chartInstance
                }}
              />
            </div>
          </div>
          <div className='Index-right-center'>
            <div className='Index-right-title'>
              <span>{t('site.energyBalance')}</span>
              {/* <img src="/assets/image/qp.svg" /> */}
            </div>
            <div className='Index-right-center-content'>
              <div className='Index-right-center-left'>
                <EChartComponent staticOption={pieStaticOption.current} dynamicOption={leftPieDynamicOption} />
                <div className='Total-span'>
                  <span>{t('common.total')}</span>
                  <span>{chargeSum.toFixed(2)} kWh</span>
                </div>
                {/* <div className='Total-span2'>
                  {t('site.systemProduction')}:<span className='value'>{chargeSum.toFixed(2)}</span>
                  kWh
                </div> */}
              </div>
              <div className='Index-right-center-left Index-right-center-right'>
                <EChartComponent staticOption={pieStaticOption.current} dynamicOption={rightPieDynamicOption} />
                <div className='Total-span'>
                  <span>{t('common.total')}</span>
                  <span>{useSum.toFixed(2)} kWh</span>
                </div>
                {/* <div className='Total-span2'>
                  {t('site.consumption')}:<span className='value'>{useSum.toFixed(2)}</span>
                  kWh
                </div> */}
              </div>
            </div>
          </div>
        </div>
      </Flex>
    </div>
  )
}

export default Station
